/* BriefSnapshotCard — read-only mirror of the Creative Brief the
   creator received on this wave. Renders on the Wave Control Room
   between the Campaign Performance hero and the Driving Teams
   accordion so a brand rep can eyeball "what we promised them" at a
   glance.

   Reads /api/brand/waves/{id}/brief and prefers `frozen_at_send.brief`
   when the wave has been sent (audit-trail authoritative). Falls
   back to the live `brief` when the wave is still a draft.

   Cross-plane: the response includes `schema_fingerprint` — surfaced
   in the bottom-right pill so an operator can confirm registry parity
   in one glance. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Shield, Copy, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#00F5FF';

export default function BriefSnapshotCard({ waveId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!waveId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(
          `${API}/api/brand/waves/${waveId}/brief`,
          { withCredentials: true },
        );
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setError('Could not load brief.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [waveId]);

  if (loading || error) return null;
  // Authoritative brief = frozen_at_send if available, else live.
  const brief = data?.frozen_at_send_brief || data?.brief;
  if (!brief) return null;

  const frozen = !!data?.frozen_at_send_brief;
  const clonedFrom = data?.brief_cloned_from;
  const fingerprint = data?.schema_fingerprint;

  return (
    <section
      className="rounded-2xl bg-[#0F0F0F] border border-[#1f1f1f] overflow-hidden"
      data-testid="brief-snapshot-card"
    >
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full px-5 py-4 flex items-center gap-4 text-left transition-colors hover:bg-white/5"
        data-testid="brief-snapshot-toggle"
      >
        <div
          className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
          style={{ background: 'rgba(0,245,255,0.12)' }}
        >
          <FileText className="h-4 w-4" style={{ color: T }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-black text-white">Creative Brief</h3>
            {frozen && (
              <span
                className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1"
                style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80' }}
                data-testid="brief-snapshot-frozen"
              >
                <Shield className="h-2.5 w-2.5" />
                Frozen at send
              </span>
            )}
            {clonedFrom?.wave_name && (
              <span
                className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1"
                style={{ background: 'rgba(0,245,255,0.1)', color: T }}
                data-testid="brief-snapshot-cloned-from"
                title={`Cloned from ${clonedFrom.wave_name}`}
              >
                <Copy className="h-2.5 w-2.5" />
                Cloned from {clonedFrom.wave_name}
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500 truncate mt-0.5">
            {brief.message_points?.[0] || brief.format_spec || 'No content'}
          </p>
        </div>
        {expanded
          ? <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0" />
          : <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-1 space-y-4" data-testid="brief-snapshot-body">
          <SnapshotField label="Format spec">
            <p className="text-xs text-white leading-relaxed">{brief.format_spec}</p>
          </SnapshotField>
          <SnapshotField label={`Message points (${(brief.message_points || []).length})`}>
            <ul className="space-y-1.5">
              {(brief.message_points || []).map((pt, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-[10px] font-black mt-0.5 w-4 text-center flex-shrink-0" style={{ color: T }}>
                    {idx + 1}.
                  </span>
                  <span className="text-xs text-white leading-relaxed">{pt}</span>
                </li>
              ))}
            </ul>
          </SnapshotField>
          <SnapshotField label="Creative freedom">
            <p className="text-xs text-white leading-relaxed">{brief.creative_freedom}</p>
          </SnapshotField>

          {fingerprint && (
            <div className="flex items-center justify-end gap-1.5 pt-1">
              <span className="text-[9px] uppercase tracking-widest text-gray-600 font-bold">
                schema {data?.schema_version || 'v1'}
              </span>
              <span
                className="text-[9px] font-mono text-gray-500 truncate max-w-[18ch]"
                title={`Cross-plane brief schema fingerprint · ${fingerprint}`}
                data-testid="brief-snapshot-fingerprint"
              >
                {fingerprint.slice(0, 8)}…{fingerprint.slice(-4)}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SnapshotField({ label, children }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest text-gray-600 font-black mb-1.5">{label}</p>
      {children}
    </div>
  );
}
